import { camelize, snakeize } from 'casing'
import type { AxiosError } from 'axios'
import type { Dispatch } from 'redux'

import {
  fetchUsers,
  fetchUsersFromIdentityService,
  updateUserData,
  updateUserFromIdentityService,
} from '../../api/users'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import { errorLogger, type LoggerGlobalState } from '../../utils/errorLogger'
import { getErrorMessages } from '../../utils/error'
import {
  InviteUserUpdateRequestSuccess,
  RequestFailureMessage,
} from '../../utils/messagesContants'
import { PartnerType } from '../../types/partner'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import { updateAppAlert } from '../appAlert/actions'
import { AuthProvider } from '../userProfile/userProfileTypes'
import type { UserState } from '../../utils/hooks/useGetUsers'
import { userListErrorMessage } from './utils'

const SET_USERS = 'SET_USERS'
const SET_ERROR_MESS = 'SET_ERROR_MESS'
const SET_START_USER_LOADING = 'SET_START_USER_LOADING'
const SET_STOP_USER_LOADING = 'SET_STOP_USER_LOADING'

type GetUsersParams = {
  partnerId: string
  pageNumber?: number
  pageSize?: number
  authProvider: AuthProvider
}

type UserListPayload = {
  users: UserState[]
  count: number
}

type IdentityServiceUserRow = {
  id?: string
  firstname?: string
  lastname?: string
  email?: string
  idp_user_id?: string
  roles?: UserState['roles']
  status?: UserState['status']
  invitation_status?: UserState['invitationStatus']
  created_at?: string
}

type UserListThunkState = LoggerGlobalState

const setUsers = (
  userList: UserListPayload,
  partnerType: PartnerType = PartnerType.User,
  pageSize?: number,
  pageNumber?: number
) =>
  actionTypeWrapper(partnerType, {
    type: SET_USERS,
    payload: { userList: camelize(userList), pageSize, pageNumber },
  })

const setUsersErrorMess = (
  message: string,
  partnerType: PartnerType = PartnerType.User
) =>
  actionTypeWrapper(partnerType, {
    type: SET_ERROR_MESS,
    payload: message,
  })

export const startUserLoading = (
  partnerType: PartnerType = PartnerType.User
) =>
  actionTypeWrapper(partnerType, {
    type: SET_START_USER_LOADING,
  })

export const stopUserLoading = () => ({
  type: SET_STOP_USER_LOADING,
})

export const getUsers =
  (
    args: GetUsersParams,
    partnerObjectType: PartnerType = PartnerType.User
  ) =>
  async (dispatch: Dispatch, getState: () => UserListThunkState) => {
    try {
      const { pageNumber, pageSize } = args
      dispatch(startLoading(LoadingTypes.GET_USERS))
      let responseData: UserListPayload
      if (args.authProvider == AuthProvider.AUTH0) {
        const { data } = await fetchUsersFromIdentityService(args)
        responseData = {
          count: data?.metadata?.total || 0,
          users:
            data?.data?.map((user: IdentityServiceUserRow) => ({
              id: user?.id || '',
              name: (user?.firstname || '') + ' ' + (user?.lastname || ''),
              email: user?.email || '',
              idpUserId: user?.idp_user_id || '',
              roles: user?.roles || [],
              status: user?.status,
              invitationStatus: user?.invitation_status,
              createdAt: user?.created_at || '',
              firstName: user?.firstname || '',
              lastName: user?.lastname || '',
            })) || [],
        }
      } else {
        const { data } = await fetchUsers(args)
        responseData = {
          count: data?.count || 0,
          users: data?.users || [],
        }
      }
      dispatch(
        setUsers(
          responseData,
          partnerObjectType,
          pageSize as number,
          pageNumber as number
        )
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )
      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(setUsersErrorMess(errMess, partnerObjectType))
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_USERS))
    }
  }

export const updateUser =
  (
    partnerId: string,
    userId: string,
    data: Partial<UserState>,
    authProvider: AuthProvider,
    updateSuccessMsg?: string,
    partnerObjectType: PartnerType = PartnerType.User
  ) =>
  async (dispatch: Dispatch, getState: () => UserListThunkState) => {
    try {
      dispatch(startUserLoading())
      if (authProvider === AuthProvider.OKTA) {
        await updateUserData(partnerId, userId, snakeize(data))
      } else {
        await updateUserFromIdentityService(partnerId, snakeize(data), userId)
      }
      dispatch(
        updateAppAlert({
          message: updateSuccessMsg || InviteUserUpdateRequestSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
      return {
        success: true,
      }
    } catch (err) {
      const errMess =
        ((err as AxiosError)?.response?.data as { message?: string })
          ?.message || RequestFailureMessage

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        setUsersErrorMess(
          userListErrorMessage(errMess),
          partnerObjectType
        )
      )
      dispatch(
        updateAppAlert({
          message: userListErrorMessage(errMess),
          messageType: 'ERROR',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
    } finally {
      dispatch(stopUserLoading())
    }
  }
